import type { ReactNode } from 'react'
import { useFlagValue } from '@labrav/flags'
import {
  LabraAIChatTriggerButton,
  LabraChatDock,
  FreshChatOnPageChange,
  LabraChatProvider,
  useAccountId,
  type ProductType,
} from '@labrav/common'
import { useSelector } from '../../store'
import type { ChatSamplePrompt } from './flyoutChatPrompts'
import { ChatSamplePrompts } from './ChatSamplePrompts'

interface FlyoutChatShellProps {
  /** Pill label. Use the wording already shown as the page title. */
  pageLabel: string
  composerPlaceholder: string
  prompts: ChatSamplePrompt[]
  children: ReactNode
  /** Product whose account the chat is scoped to. OppSync pages pass `oppsync`. */
  product?: ProductType
}

/**
 * Labra AI over a page. Add a page by wrapping its layout and passing that
 * page's sample prompts — the provider, dock, and greeting stay here.
 */
export const FlyoutChatShell = ({
  pageLabel,
  composerPlaceholder,
  prompts,
  children,
  product = 'flyout',
}: FlyoutChatShellProps) => {
  const selectedAccountId = useAccountId({ product })
  const partnerId = useSelector<string>(
    state =>
      state.AccountData.user.partnerData?.partnerId || selectedAccountId
  )
  // Flyout chat is scoped to the selected cloud account. OppSync pages, including
  // propensity, use the Labra partner id the way CoSell chat does.
  const accountId = product === 'flyout' ? selectedAccountId : partnerId
  const userId = useSelector<string>(
    state => state.userProfile.userProfile?.id ?? ''
  )
  const userFirstName = useSelector<string | undefined>(
    state => state.userProfile.userProfile?.firstName
  )
  const isChatRevampEnabled = useFlagValue('chatRevamp')?.value ?? false

  return (
    <LabraChatProvider
      partnerId={partnerId}
      accountId={accountId}
      userId={userId}
      agentType="supervisor_agent"
    >
      <FreshChatOnPageChange pageKey={`${pageLabel}:${selectedAccountId}`} />
      <LabraChatDock
        variant="floating"
        userName={userFirstName}
        pageContextLabel={pageLabel}
        hideDefaultEmpty
        emptyContent={
          <ChatSamplePrompts userName={userFirstName} prompts={prompts} />
        }
        composerPlaceholder={composerPlaceholder}
      >
        {children}
      </LabraChatDock>
      {isChatRevampEnabled && <LabraAIChatTriggerButton label="Ask Labra AI" />}
    </LabraChatProvider>
  )
}
